import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Icon from "../icon/Icon";
import type { TableProps } from "./Table.types";
import "./table.scss";
import Button from "../button/Button";

const Table = ({
  children,
  subChildren,
  hasBorder,
  color,
  customColor,
  className,
  textIconOpen,
  textIconClose,
}: TableProps) => {
  const isBorder = hasBorder ? "sb-table--border" : "";
  const hasChildren = !!subChildren ? "sb-table__children" : "";
  const defaultBorderColor =
    hasBorder && !customColor ? `sb-table--border-${color}` : "";
  const [isOpen, setIsOpen] = useState(false);
  const open = isOpen ? "sb-table--open" : "";
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (customColor && tableRef.current) {
      tableRef.current.style.setProperty("--bg-border-color", customColor);
    }
  }, [tableRef, customColor]);

  return (
    <div
      className={["sb-table", isBorder, className, hasChildren, open].join(" ")}
      data-testid="sb-table"
    >
      <div
        className={`sb-table__container ${defaultBorderColor}`}
        ref={tableRef}
        data-testid="sb-table__container"
      >
        <div
          className="sb-table__container--content"
          data-testid="sb-table__container--content"
        >
          {children}
        </div>

        {hasChildren && (
          <div className="sb-table__container--button">
            <span>
              {isOpen ? textIconOpen : textIconClose}
            </span>
            <Icon
              iconName={isOpen ? "chevron-up" : "chevron-down"}
              size={18}
              className="sb-table__icon"
              onClick={() => {
                setIsOpen(!isOpen);
              }}
            />
          </div>
        )}
      </div>
      {hasChildren && (
        <div className="sb-table__child" data-testid="sb-table__child">
          {subChildren}
        </div>
      )}
    </div>
  );
};

Table.propTypes = {
  /**
   * Table children
   */
  children: PropTypes.object,
  /**
   * Table children
   */
  subChildren: PropTypes.object,
  /**
   * className
   */
  className: PropTypes.string,
  /**
   * customColor
   */
  customColor: PropTypes.string,
  /**
   * Color of border
   */
  color: PropTypes.oneOf(["primary", "secondary", "tertiary"]),
  /**
   * hasBorder?
   */
  hasBorder: PropTypes.bool,
  /**
   * textIconOpen
   */
  textIconOpen: PropTypes.string,
  /**
   * textIconOpen
   */
  textIconClose: PropTypes.string,
};

Table.defaultProps = {
  children: (
    <div className="sb-table--row">
      <div>content 1</div>
      <div>content 2</div>
    </div>
  ),
  subChildren: null,
  className: "",
  color: "primary",
  customColor: "",
  hasBorder: false,
  textIconOpen: "",
  textIconClose: "",
};

export default Table;
